import { getDefaultProfileAvatar, DEFAULT_PROFILE_AVATARS } from '../utils/defaultProfileAvatar';

// API Configuration for React app
const API_CONFIG = {
  // Base URL for API calls, 개발 환경에서는 프록시 사용, 프로덕션에서는 직접 연결
  BASE_URL: process.env.NODE_ENV === 'development' ? '' : (process.env.REACT_APP_API_URL || 'https://api.astrantis.net'),
  
  // Base URL for file URLs (uploads, images). Empty = same-origin. Step 5 이후 파일 서비스 URL 설정.
  FILE_BASE_URL: process.env.REACT_APP_FILE_PUBLIC_BASE_URL || '',

  // 기본 프로필 아바타 (userId 없을 때 A.svg). userId가 있으면 getDefaultAvatar 사용.
  DEFAULT_AVATAR: DEFAULT_PROFILE_AVATARS[0],
  getDefaultAvatar: getDefaultProfileAvatar,

  // API prefix for backend routes
  API_PREFIX: '/api',
  
  // WebSocket configuration
  WS_URL: process.env.REACT_APP_WS_URL || 'ws://api.astrantis.net/ws',
  WSS_URL: process.env.REACT_APP_WSS_URL || 'wss://api.astrantis.net/ws',
  
  // Environment
  ENVIRONMENT: process.env.REACT_APP_ENVIRONMENT || 'development',
  
  // Helper function to get full API URL
  getApiUrl: (endpoint) => {
    const baseUrl = API_CONFIG.BASE_URL;
    const apiPrefix = API_CONFIG.API_PREFIX;
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    return `${baseUrl}${apiPrefix}${cleanEndpoint}`;
  },
  
  // Helper function to get WebSocket URL
  getWebSocketUrl: () => {
    const isSecure = window.location.protocol === 'https:';
    return isSecure ? API_CONFIG.WSS_URL : API_CONFIG.WS_URL;
  },
  
  /**
   * 단일 파일 URL 해석: 절대 URL은 유지, 상대 경로는 적절한 base와 조합. 빈 값·null → ''.
   * /file-ref/* 및 /api/public-file/* 는 백엔드 라우트이므로 REACT_APP_API_URL 사용.
   * (공개 게시글 본문은 /api/public-file/post/:id/content/:n 형태 — Pages에서 상대 경로면 astrantis.net으로 가서 이미지가 깨짐.)
   * 그 외 상대 경로는 FILE_BASE_URL(파일 서비스) 사용.
   */
  resolveFileUrl(filePath) {
    if (!filePath || !String(filePath).trim()) return '';
    if (/^https?:\/\//i.test(filePath)) return filePath;
    const raw = filePath.startsWith('/') ? filePath : `/${filePath}`;
    let clean;
    try {
      clean = new URL(raw, 'https://placeholder.invalid').pathname;
    } catch {
      clean = raw;
    }
    const needsApiBase =
      clean.startsWith('/file-ref/') || clean.startsWith('/api/public-file/');
    const base = needsApiBase
      ? (process.env.REACT_APP_API_URL || '').replace(/\/$/, '')
      : (API_CONFIG.FILE_BASE_URL || '').replace(/\/$/, '');
    return base ? `${base}${clean}` : clean;
  },

  /** post 본문 HTML의 img src 등을 resolveFileUrl로 백엔드·파일 베이스에 맞춤 */
  rewriteHtmlMediaUrls(html) {
    if (html == null || typeof html !== 'string') return html ?? '';
    return html.replace(/\bsrc\s*=\s*(["'])([^"']*)\1/gi, (_, quote, url) => {
      const u = (url || '').trim();
      const resolved = this.resolveFileUrl(u);
      return `src=${quote}${resolved || u}${quote}`;
    });
  },

  // Swagger/OpenAPI configuration
  OPENAPI_YAML_URL: process.env.PUBLIC_URL 
    ? `${process.env.PUBLIC_URL}/openapi.yaml` 
    : '/openapi.yaml',
  
  SWAGGER_SERVERS: [
    {
      url: process.env.NODE_ENV === 'development'
        ? 'http://localhost:8081'
        : (process.env.REACT_APP_API_URL || 'https://api.astrantis.net'),
      description: process.env.NODE_ENV === 'development'
        ? 'Development Server'
        : 'Production Server (HTTPS)'
    },
    {
      url: process.env.NODE_ENV === 'development'
        ? 'https://localhost:8443'
        : (process.env.REACT_APP_API_URL || 'https://api.astrantis.net').replace(/^https/, 'http'),
      description: process.env.NODE_ENV === 'development'
        ? 'Development Server (HTTPS)'
        : 'Production Server (HTTP)'
    }
  ],

  // Helper function to get Swagger server URL
  getSwaggerServerUrl: () => {
    if (process.env.NODE_ENV === 'development') {
      return window.location.protocol === 'https:' 
        ? 'https://localhost:8443' 
        : 'http://localhost:8081';
    }
    const base = process.env.REACT_APP_API_URL || 'https://api.astrantis.net';
    return window.location.protocol === 'https:' ? base : base.replace(/^https/, 'http');
  }
};

export default API_CONFIG;
